import { useContext } from 'react';

import { Node } from 'slate';
import { ReactEditor, RenderElementProps, useSlateStatic } from 'slate-react';

import { CodeBlockCopyButton } from './CodeBlockCopyButton';
import './codeBlock.css';
import {
  CodeIndentContext,
  codeBlockText,
  codeGutterStyle,
  codeIndentGuides,
  codeIndentStyle,
} from './codeHighlight';
import ColumnsComponent from './elements/ColumnsComponent';
import ImageComponent from './elements/ImageComponent';
import LinkComponent from './elements/LinkComponent';
import MentionComponent from './elements/MentionComponent';
import { CustomElement } from './types';

const CodeLineElement = ({ attributes, children, element }: RenderElementProps) => {
  const editor = useSlateStatic();
  const guides = useContext(CodeIndentContext);
  let index = 0;
  try {
    const path = ReactEditor.findPath(editor, element);
    index = path[path.length - 1];
  } catch {
    // node not mounted yet — render without guides
  }

  return (
    <div
      {...attributes}
      style={codeIndentStyle(guides?.levels[index], guides?.unit)}
      className="rte-code-line whitespace-pre"
    >
      {children}
    </div>
  );
};

const RenderElement = (props: RenderElementProps) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'link':
      return <LinkComponent {...props} />;
    case 'image':
      return <ImageComponent {...props} />;
    case 'mention':
      return <MentionComponent {...props} />;
    case 'bulleted-list':
      return (
        <ul className="list-disc list-inside" {...attributes}>
          {children}
        </ul>
      );
    case 'numbered-list':
      return (
        <ol className="list-decimal list-inside" {...attributes}>
          {children}
        </ol>
      );
    case 'list-item':
      return (
        <li className="font-normal" {...attributes}>
          {children}
        </li>
      );
    case 'columns':
      return <ColumnsComponent {...props} />;
    case 'column':
      return <>{children}</>;
    case 'code-block': {
      const guides = codeIndentGuides(
        (element as CustomElement).children.map((line) => Node.string(line)),
      );
      return (
        <pre
          {...attributes}
          style={codeGutterStyle(element.children.length)}
          className="rte-code-block group relative my-1 overflow-x-auto rounded-[8px] border border-[#2d2d2d] bg-[#1e1e1e] py-2.5 pl-0 pr-3 font-mono text-[13px] leading-[1.6] text-[#d4d4d4]"
        >
          <CodeIndentContext.Provider value={guides}>{children}</CodeIndentContext.Provider>
          <CodeBlockCopyButton getText={() => codeBlockText(props.element as CustomElement)} />
        </pre>
      );
    }
    case 'code-line':
      return <CodeLineElement {...props} />;

    default:
      return <p {...attributes}>{children}</p>;
  }
};

export default RenderElement;
